import { getFormattedTimeRange } from "./DateFilterPicker";
import { InvoiceDisplayStatus, InvoicePaymentDisplayStatusType, InvoiceSortingColumn } from 'anchor-scout/types';
import { InvoiceOptimisticUpdateType } from "../FullPageInvoice/FullPageInvoice.utils";
import { invoiceModel } from "../../models";
import { QboStatusFilter } from "../../business-logic/qboStatusFilterLogic";
export let InvoiceHeaderItemID = /*#__PURE__*/function (InvoiceHeaderItemID) {
  InvoiceHeaderItemID["invoiceId"] = "invoiceId";
  InvoiceHeaderItemID["companyName"] = "companyName";
  InvoiceHeaderItemID["status"] = "status";
  InvoiceHeaderItemID["issueDate"] = "issueDate";
  InvoiceHeaderItemID["dueDate"] = "dueDate";
  InvoiceHeaderItemID["total"] = "total";
  InvoiceHeaderItemID["additionalInformation"] = "additionalInformation";
  return InvoiceHeaderItemID;
}({});
export const invoiceHeaderItemIdToSortingColumn = {
  [InvoiceHeaderItemID.invoiceId]: InvoiceSortingColumn.Number,
  [InvoiceHeaderItemID.companyName]: InvoiceSortingColumn.CompanyName,
  [InvoiceHeaderItemID.status]: InvoiceSortingColumn.Status,
  [InvoiceHeaderItemID.additionalInformation]: InvoiceSortingColumn.PaymentStatus,
  [InvoiceHeaderItemID.issueDate]: InvoiceSortingColumn.IssuedOn,
  [InvoiceHeaderItemID.dueDate]: InvoiceSortingColumn.DueDate,
  [InvoiceHeaderItemID.total]: InvoiceSortingColumn.TotalAmount
};
const invoiceDisplayStatusToLabel = {
  [InvoiceDisplayStatus.Issued]: 'Issued',
  [InvoiceDisplayStatus.Overdue]: 'Overdue',
  [InvoiceDisplayStatus.PaymentInitiated]: 'Processing',
  [InvoiceDisplayStatus.Paid]: 'Paid',
  [InvoiceDisplayStatus.Voided]: 'Voided',
  [InvoiceDisplayStatus.Refunded]: 'Refunded',
  [InvoiceDisplayStatus.RefundFailed]: 'Refund failed',
  [InvoiceDisplayStatus.PaymentDisputed]: 'Disputed'
};
export function getPaymentStatusFilterOptions(isVendor, paymentStatusFilterCounters) {
  return [InvoicePaymentDisplayStatusType.StoppedPayment, InvoicePaymentDisplayStatusType.RequiresPaymentApproval, InvoicePaymentDisplayStatusType.RequiresPaymentValidation, InvoicePaymentDisplayStatusType.PaymentFailed, InvoicePaymentDisplayStatusType.PaymentMethodNotSet, InvoicePaymentDisplayStatusType.PaymentMethodUnverified, InvoicePaymentDisplayStatusType.Processing, InvoicePaymentDisplayStatusType.Collected, InvoicePaymentDisplayStatusType.Paid, InvoicePaymentDisplayStatusType.PaidExternally, InvoicePaymentDisplayStatusType.Refunded, InvoicePaymentDisplayStatusType.Disputed, InvoicePaymentDisplayStatusType.RefundRequested, InvoicePaymentDisplayStatusType.None].map(status => ({
    value: status,
    label: getPaymentStatusFilterText(status, isVendor),
    counter: paymentStatusFilterCounters[status] || 0
  })).filter(opt => opt.counter);
}
function getPaymentStatusFilterText(status, isVendor) {
  switch (status) {
    case InvoicePaymentDisplayStatusType.None:
      return 'None';
    case InvoicePaymentDisplayStatusType.Collected:
      return 'Collected';
    case InvoicePaymentDisplayStatusType.Paid:
      return 'Deposited';
    case InvoicePaymentDisplayStatusType.PaidExternally:
      return 'Paid externally';
    case InvoicePaymentDisplayStatusType.PaymentFailed:
      return 'Payment failed';
    case InvoicePaymentDisplayStatusType.PaymentMethodNotSet:
      return 'Payment method not set';
    case InvoicePaymentDisplayStatusType.PaymentMethodUnverified:
      return 'Payment method not verified';
    case InvoicePaymentDisplayStatusType.Pending:
      return 'Issued';
    case InvoicePaymentDisplayStatusType.Processing:
      return 'Payment initiated';
    case InvoicePaymentDisplayStatusType.Refunded:
      return 'Refund initiated';
    case InvoicePaymentDisplayStatusType.RefundFailed:
      return 'Refund failed';
    case InvoicePaymentDisplayStatusType.RequiresPaymentApproval:
      return isVendor ? 'Client approval required' : 'Pending your approval';
    case InvoicePaymentDisplayStatusType.RequiresPaymentValidation:
      return 'Payment validation required';
    case InvoicePaymentDisplayStatusType.StoppedPayment:
      return 'Client stopped payment';
    case InvoicePaymentDisplayStatusType.Disputed:
      return 'Disputed';
    case InvoicePaymentDisplayStatusType.RefundRequested:
      return 'Refund requested';
  }
}
export function getInvoiceStatusFilterOptions(statusFilterCounters) {
  return [InvoiceDisplayStatus.Issued, InvoiceDisplayStatus.PaymentInitiated, InvoiceDisplayStatus.Paid, InvoiceDisplayStatus.Overdue, InvoiceDisplayStatus.Voided, InvoiceDisplayStatus.Refunded, InvoiceDisplayStatus.RefundFailed, InvoiceDisplayStatus.PaymentDisputed].map(statusId => ({
    value: statusId,
    label: invoiceDisplayStatusToLabel[statusId],
    counter: statusFilterCounters[statusId] || 0
  })).filter(opt => opt.counter);
}
export function getQboStatusFilterOptions(qboStatusFilterCounters) {
  const syncedCounter = qboStatusFilterCounters.sync || 0;
  const unsyncedCounter = qboStatusFilterCounters.unsync || 0;
  return [{
    value: QboStatusFilter.All,
    label: 'All',
    counter: syncedCounter + unsyncedCounter
  }, {
    value: QboStatusFilter.Synced,
    label: 'Synced',
    counter: syncedCounter
  }, {
    value: QboStatusFilter.NotSynced,
    label: 'Not synced',
    counter: unsyncedCounter
  }].filter(opt => opt.counter);
}
export function applyOptimisticUpdate(invoices, optimisticUpdate, now) {
  const {
    type,
    invoiceId
  } = optimisticUpdate;
  const updateInvoice = update => invoices.map(invoice => invoice.id === invoiceId ? update(invoice) : invoice);
  switch (type) {
    case InvoiceOptimisticUpdateType.Refunded:
      return updateInvoice(inv => invoiceModel.setRefunded(inv, optimisticUpdate.timestamp));
    case InvoiceOptimisticUpdateType.RefundRequested:
      {
        const {
          userId,
          reason
        } = optimisticUpdate;
        return updateInvoice(inv => invoiceModel.setRefundRequested(inv, userId, reason));
      }
    case InvoiceOptimisticUpdateType.Voided:
      return updateInvoice(invoiceModel.setVoided);
    case InvoiceOptimisticUpdateType.PaymentApproved:
      return updateInvoice(inv => invoiceModel.setPending(inv, now));
    case InvoiceOptimisticUpdateType.PaymentConfirmed:
      {
        const {
          isVendor
        } = optimisticUpdate;
        return updateInvoice(inv => invoiceModel.setPaymentConfirmed(inv, isVendor));
      }
  }
}
export function mapSortAndFiltersRequestParams(_ref) {
  let {
    date,
    now,
    sort,
    sortIds,
    search,
    status,
    paymentStatus,
    qbo
  } = _ref;
  const {
    fromDate,
    toDate
  } = getFormattedTimeRange(date, now);
  return {
    search,
    status,
    paymentStatus,
    integration: qbo,
    issuedOnSince: fromDate ? new Date(fromDate) : undefined,
    issuedOnUntil: toDate ? new Date(toDate) : undefined,
    sort: sort ? {
      field: invoiceHeaderItemIdToSortingColumn[sortIds[sort.colIndex]],
      type: sort.sort
    } : undefined
  };
}